import { useState, useEffect, useCallback, useMemo } from "react";
// services
import {
  getData,
  deleteData,
} from "../../../services/event/registrationSponsorship/registrationSponsorshipEnquiryDetailsServices";

import DataTable from "react-data-table-component";
import Header from "../../../components/Layout/Header";
import Footer from "../../../components/Layout/Footer";
import Sidebar from "../../../components/Layout/Sidebar";
import Spinner from "../../../components/Spinner";
import { Button, Modal } from "react-bootstrap";
import { formatDate } from "../../../utils/helper";

const subDomain =
  localStorage.getItem("sub_domain") ||
  localStorage.getItem("cms_sub_domain") ||
  "";

const RegistrationSponsorshipInquiryList = () => {
  const [dataList, setDataList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  // --------------------------------------------------
  // Fetch Registration Sponsorship Data
  // --------------------------------------------------
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getData({
        sub_domain: subDomain,
      });

      if (data && data.length === 0) {
        setDataList([]);
      }
      setDataList(data || []);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch registration sponsorship data:", error);
      setDataList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // --------------------------------------------------
  // Initial Load
  // --------------------------------------------------
  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true);

      await Promise.all([fetchData()]);

      await new Promise((resolve) => setTimeout(resolve, 1000));

      setLoading(false);
    };

    fetchDataAsync();
  }, [fetchData]);

  // --------------------------------------------------
  // Search
  // --------------------------------------------------
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  // --------------------------------------------------
  // Pagination
  // --------------------------------------------------
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // --------------------------------------------------
  // Delete Confirmation Modal
  // --------------------------------------------------
  const handleDelete = async () => {
    if (selectedData) {
      // Optimistically remove the data from the list
      setDataList((prev) => prev.filter((data) => data.id !== selectedData.id));
      // Proceed with the actual delete request
      await deleteData(selectedData);
      setShowDeleteConfirmModal(false);
      fetchData();
    }
  };

  const openDeleteConfirmModal = (data) => {
    setSelectedData(data);
    setShowDeleteConfirmModal(true);
  };

  // --------------------------------------------------
  // Filter Data
  // --------------------------------------------------
  const filteredData = useMemo(() => {
    const search = searchText.toLowerCase().trim();

    const startDateObj = startDate ? new Date(`${startDate}T00:00:00`) : null;

    const endDateObj = endDate ? new Date(`${endDate}T23:59:59`) : null;

    return dataList
      ?.filter((data) => {
        // --------------------------------------------
        // Search fields
        // --------------------------------------------
        const searchableText = [
          data.sponsor_id || data.sponser_id,
          data.inquiry_reason,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        const matchesText = !search || searchableText.includes(search);

        // --------------------------------------------
        // Date filtering
        // --------------------------------------------
        const date = data.createdAt ? new Date(data.createdAt) : null;

        let inRange = true;

        if (startDateObj && date) {
          inRange = date >= startDateObj;
        }

        if (endDateObj && date) {
          inRange = inRange && date <= endDateObj;
        }

        // --------------------------------------------
        // Country filtering
        // --------------------------------------------

        return matchesText && inRange;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [dataList, searchText, startDate, endDate]);

  // --------------------------------------------------
  // DataTable Columns
  // --------------------------------------------------
  const columns = [
    {
      name: "#",
      selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
      sortable: false,
      width: "60px",
    },
    {
      name: "Sponsor ID",
      selector: (row) => row.sponsor_id || "-",
      sortable: true,
      width: "150px",
      wrap: true,
    },
    {
      name: "Inquiry Reason",
      selector: (row) => row.inquiry_reason || "-",
      sortable: true,
      width: "150px",
      wrap: true,
    },
    {
      name: "Offered  Date",
      selector: (row) => formatDate(row.offer_created_at),
      sortable: true,
      width: "160px",
      wrap: true,
    },
    {
      name: "Expired  date",
      selector: (row) => formatDate(row.expired_date),
      sortable: true,
      width: "160px",
      wrap: true,
    },

    // {
    //   name: "Actions",
    //   cell: (row) => (
    //     <div>
    //       <Button
    //     className="me-2"
    //     variant="warning"
    //     size="sm"
    //     onClick={() =>
    //       navigate(`//${row.sponsor_id}`)
    //     }
    //   >
    //     Edit
    //   </Button>
    //       <Button
    //         variant="danger"
    //         size="sm"
    //         onClick={() => openDeleteConfirmModal(row)}
    //       >
    //         Delete
    //       </Button>
    //     </div>
    //   ),
    //   width: "25%",
    //   wrap: true,
    // },
  ];

  // --------------------------------------------------
  // Expandable Row
  // --------------------------------------------------
  const ExpandableRow = ({ data }) => {
    return (
      <div className="p-3 bg-light border">
        <table className="table table-bordered table-striped">
          <tbody>
            {/* Sponsorship Inquiry Information */}
            <tr>
              <td>
                <b>Sponsor ID</b>
              </td>
              <td>{data.sponsor_id || data.sponser_id || "-"}</td>
            </tr>

            <tr>
              <td>
                <b>Inquiry Reason</b>
              </td>
              <td>{data.inquiry_reason || "-"}</td>
            </tr>

            <tr>
              <td>
                <b>Note</b>
              </td>
              <td>{data.user_notes || "-"}</td>
            </tr>

            <tr>
              <td>
                <b>Offered Date</b>
              </td>
              <td>{data.offer_created_at || "-"}</td>
            </tr>

            <tr>
              <td>
                <b>Expiry Date</b>
              </td>
              <td>{data.expired_date || "-"}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // --------------------------------------------------
  // JSX
  // --------------------------------------------------
  return (
    <div className="d-flex">
      <Sidebar />

      <div className="main-content w-100">
        <Header />

        {/* Filters */}
        <div className="row m-3 d-flex flex-column gap-3">
          <div className="d-flex gap-2">
            {/* Start Date */}
            <div className="col-md-2">
              <span className="fs-5 text-dark">Start Date</span>

              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            {/* End Date */}
            <div className="col-md-2">
              <span className="fs-5 text-dark">End Date</span>

              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center w-100"
            style={{ minHeight: "300px" }}
          >
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className="fs-4 text-dark">
                    Conference Leads / Sponsorship Inquiries
                  </p>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                highlightOnHover
                responsive
                striped
                paginationPerPage={10}
                paginationComponentOptions={{
                  noRowsPerPage: true,
                }}
                onChangePage={handlePageChange}
                subHeader
                subHeaderComponent={
                  <div>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search"
                      value={searchText}
                      onChange={handleSearchChange}
                    />
                  </div>
                }
                expandableRows
                expandableRowsComponent={ExpandableRow}
              />
              {/* Delete Confirmation Modal */}
              <Modal
                show={showDeleteConfirmModal}
                onHide={() => setShowDeleteConfirmModal(false)}
              >
                <Modal.Header closeButton>
                  <Modal.Title>Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <p>Are you sure you want to delete this row?</p>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowDeleteConfirmModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button variant="danger" onClick={handleDelete}>
                    Yes, Delete
                  </Button>
                </Modal.Footer>
              </Modal>
            </div>

            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};
export default RegistrationSponsorshipInquiryList;
